import { FadeIn, Skeleton } from "@xola/ui-kit";
import { isNumber } from "lodash";
import React from "react";
import { useQuery } from "react-query";
import shallow from "zustand/shallow";
import { useSeller } from "../../../hooks/useAccount";
import { getGiftReport, GiftReportValues } from "../../../reports/GiftReport";
import { useGiftFilterStore } from "../../../stores/reports/giftsReportFilterStore";
import { Currency } from "../../Common/Currency";

export const GiftReportSummary = () => {
    const seller = useSeller();
    const type = useGiftFilterStore((state) => state.type);
    const [from, to] = useGiftFilterStore((state) => [state.getFrom(), state.getTo()], shallow);

    const params = { seller, from, to, type };
    const { data, isLoading } = useQuery(["giftReport", params], async () => getGiftReport(params), {
        staleTime: 100,
    });

    if (isLoading || !data) {
        return <GiftReportSkeleton />;
    }

    const methods = [
        { method: GiftReportValues.AMOUNT_REDEEMED, label: "Amount Redeemed" },
        { method: GiftReportValues.OUTSTANDING_BALANCE, label: "Outstanding Balance" },
        { method: GiftReportValues.RECOGNIZED, label: "Recognized" },
        { method: GiftReportValues.DEFERRED, label: "Deferred" },
    ];

    return (
        <FadeIn>
            <div className="w-full px-6 py-11">
                <div className="flex h-32 w-full flex-row justify-between space-x-3">
                    <div className="flex flex-col items-center justify-center space-y-2 rounded bg-green-lighter py-8 px-6">
                        <span className="font-bold">Total</span>
                        <div className="text-2xl font-bold" data-testid="gift-report-summary-total">
                            <Currency.Split currency={seller.currency}>{data.totalAmount}</Currency.Split>
                        </div>
                    </div>
                    <div className="flex w-full flex-row justify-start space-x-12 rounded border border-gray-light py-9 px-8">
                        {methods.map(({ method, label }) => (
                            <GiftReportMethod key={method} value={data[method]} method={label} />
                        ))}
                    </div>
                </div>
            </div>
        </FadeIn>
    );
};

const GiftReportMethod = ({ method, value }: { method: string; value: number }) => {
    const { currency } = useSeller();

    return (
        <div className="flex flex-grow flex-col items-center space-y-2">
            <div className="flex flex-row items-center space-x-1">
                <div className="text-sm">{method}</div>
            </div>
            <div className="text-lg font-bold" data-testid={`gift-report-summary-${method}-value`}>
                {isNumber(value) ? <Currency.Split currency={currency}>{value}</Currency.Split> : "--"}
            </div>
        </div>
    );
};

const GiftReportSkeleton = () => (
    <div className="w-full px-6 py-10">
        <div className="flex h-32 w-full flex-row justify-between space-x-2">
            <div className="w-44">
                <Skeleton height={128} />
            </div>
            <div className="w-full">
                <Skeleton height={128} />
            </div>
        </div>
    </div>
);
